import { CreditCardIcon } from "@heroicons/react/24/outline";
import type { SubscriptionPlan, SubscriptionPricing, SubscriptionTier } from "@/types";

type PricingTableProps = {
  pricing: SubscriptionPricing;
  currentPlan: SubscriptionTier;
};

export default function PricingTable({ pricing, currentPlan }: PricingTableProps) {
  return (
    <section className="pricing-table">
      <h3 className="mb-6 text-center text-xl font-semibold">Choose Your Plan</h3>

      <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
        <FreePlanCard plan={pricing.free} current={currentPlan === "free"} />
        <PlanCard name="Pro" plan={pricing.pro} current={currentPlan === "pro"} />
        <PlanCard name="Max" plan={pricing.max} current={currentPlan === "max"} highlighted={currentPlan !== "max"} />
      </div>

      <div className="mt-6 text-center text-sm text-gray-600">
        <p>
          Need more page audits?{" "}
          <a href="/contact" className="text-primary-600 hover:underline">
            Contact us
          </a>
        </p>
      </div>
    </section>
  );
}

type FreePlanCardProps = {
  plan: SubscriptionPlan;
  current: boolean;
};

function FreePlanCard({ plan, current }: FreePlanCardProps) {
  return (
    <article className={`relative rounded-lg border p-6 ${current ? "border-primary-500" : "border-gray-300"}`}>
      {current ? <CurrentPlanBadge /> : null}

      <div className="mb-4 text-center">
        <h4 className="mb-2 text-lg font-semibold">Free</h4>
        <div className="mb-1 text-3xl font-bold">${plan.price}</div>
        <div className="text-gray-600">one-time</div>
      </div>

      <ul className="mb-6 space-y-3 text-center">
        <li className="font-semibold">{plan.audit_limit} lifetime page audits</li>
        <li className="text-gray-600">{domainLimitCopy(plan.domain_limit)}</li>
        <li className="text-gray-600">Desktop and mobile</li>
      </ul>

      {current ? <CurrentPlanButton /> : null}
    </article>
  );
}

type PlanCardProps = {
  name: string;
  plan: SubscriptionPlan;
  current: boolean;
  highlighted?: boolean;
};

function PlanCard({ name, plan, current, highlighted = false }: PlanCardProps) {
  return (
    <article
      className={`relative rounded-lg border p-6 ${current || highlighted ? "border-primary-500" : "border-gray-300"}`}
    >
      {current ? <CurrentPlanBadge /> : null}
      {!current && highlighted ? (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
          <span className="rounded-full bg-primary px-3 py-1 text-sm font-medium text-white">Best Value</span>
        </div>
      ) : null}

      <div className="mb-4 text-center">
        <h4 className="mb-2 text-lg font-semibold">{name}</h4>
        <div className="mb-1 text-3xl font-bold">${plan.price}</div>
        <div className="text-gray-600">per month</div>
      </div>

      <ul className="mb-6 space-y-3 text-center">
        <li className="font-semibold">{plan.audit_limit} page audits / month</li>
        <li className="text-gray-600">{domainLimitCopy(plan.domain_limit)}</li>
        <li className="text-gray-600">Journeys after they log in</li>
        <li className="text-gray-600">Weekly UX checks on monitored pages and journeys</li>
      </ul>

      {current ? (
        <CurrentPlanButton />
      ) : (
        <a
          href={plan.payment_link}
          className="btn w-full rounded-lg bg-linear-to-r from-primary-500 to-primary-700 font-semibold text-white transition-all hover:from-primary-600 hover:to-primary-700"
          target="_blank"
          rel="noopener noreferrer"
        >
          <CreditCardIcon className="h-5 w-5" aria-hidden="true" /> Subscribe to {name}
        </a>
      )}
    </article>
  );
}

function domainLimitCopy(domainLimit: number | null) {
  if (domainLimit == null) {
    return "Unlimited domains";
  }

  return `${domainLimit} ${domainLimit === 1 ? "domain" : "domains"}`;
}

function CurrentPlanBadge() {
  return (
    <div className="absolute -top-3 left-1/2 -translate-x-1/2">
      <span className="rounded-full bg-success px-3 py-1 text-sm font-medium text-white">Current plan</span>
    </div>
  );
}

function CurrentPlanButton() {
  return (
    <button
      type="button"
      className="btn w-full cursor-default border border-gray-300 bg-gray-50 font-semibold text-gray-700"
      disabled
    >
      Current plan
    </button>
  );
}
